import type { ChatMessage, Conversation, ToolCallDisplay } from '../types/agent.types';

// Shapes returned by the skywatch-agent conversation store (GET /skywatch-agent/conversations).
// The agent persists every Hub chat there, keyed by the ecToken's user and app 'hub'.

export interface StoredConversation {
  conversationId: string;
  title?: string;
  status: 'active' | 'archived';
  messageCount: number;
  createdAt: string;
  updatedAt: string;
}

export interface StoredToolCall {
  toolName: string;
  input: Record<string, unknown>;
  status?: 'success' | 'error';
}

export interface StoredMessage {
  role: 'user' | 'assistant' | 'system';
  content: string;
  ts: string;
  seq: number;
  messageId?: string;
  toolCalls?: StoredToolCall[];
}

const AGENT_PATH = '/auth0-jwt/earthcache/agent';

/**
 * The conversation store lives on the same API as the agent's REST routes, under
 * /skywatch-agent instead of /auth0-jwt/earthcache/agent.
 */
export function conversationsBaseUrl(agentApiUrl: string): string {
  const trimmed = agentApiUrl.replace(/\/+$/, '');
  if (!trimmed.endsWith(AGENT_PATH)) {
    throw new Error('Agent API URL must end with /auth0-jwt/earthcache/agent');
  }
  return `${trimmed.slice(0, -AGENT_PATH.length)}/skywatch-agent/conversations`;
}

export function toConversation(c: StoredConversation): Conversation {
  return {
    conversationId: c.conversationId,
    // Empty when the store has none; the list shows a translated placeholder.
    title: c.title ?? '',
    status: c.status,
    messageCount: c.messageCount,
    createdAt: c.createdAt,
    updatedAt: c.updatedAt,
  };
}

// The store keeps tool inputs but not results or ids. A stable id per message and position
// lets the UI mark restored tool calls as already triggered so they do not re-run.
function toToolCall(tc: StoredToolCall, messageId: string, index: number): ToolCallDisplay {
  return {
    toolUseId: `${messageId}:tool:${index}`,
    toolName: tc.toolName,
    input: tc.input,
    status: tc.status ?? 'success',
  };
}

// Same fallback the agent uses when it reads a message back (messageId, else timestamp),
// so feedback on a restored message refers to the id the agent knows.
export function toChatMessages(messages: StoredMessage[]): ChatMessage[] {
  return messages
    .filter(
      (m): m is StoredMessage & { role: 'user' | 'assistant' } =>
        m.role === 'user' || m.role === 'assistant'
    )
    .map(m => {
      const messageId = m.messageId ?? m.ts;
      return {
        messageId,
        role: m.role,
        content: m.content,
        createdAt: m.ts,
        ...(m.toolCalls?.length && {
          toolCalls: m.toolCalls.map((tc, i) => toToolCall(tc, messageId, i)),
        }),
      };
    });
}
